import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { TypeAnimation } from 'react-type-animation'
import { useRecoilState } from 'recoil'
import { AccountCircle, ChangeCircle, Email } from '@mui/icons-material'

import { loadingState } from '../../recoil/state'
import useFormOperations from '../../hooks/useFormOperations'
import usePostData from '../../hooks/usePostData'
import useSnackbar from '../../hooks/useSnackbar'

import { ConfirmButton, PurpleButton } from '../../components/mui/button/Button'
import { CustomTextField } from '../../components/mui/Input'
import collegeLogos from '../../CollegeConfig'

const ForgotPassword = () => {

    const navigate = useNavigate();
    const { handleSnackbarOpen } = useSnackbar();
    const [, setLoading] = useRecoilState(loadingState);
    const { data: employeeEmail, postData } = usePostData();

    const [idSubmitted, setIdSubmitted] = useState(false);

    const toggleIdSubmitted = () => setIdSubmitted(!idSubmitted);

    const onSubmitId = async () => {
        const IdFormData = { employeeId: formData?.employeeId }
        setLoading(true);
        const { success, message } = await postData('/api/forgotPassword/getEmailId', IdFormData)
        setLoading(false);

        if (message) return handleSnackbarOpen(message, 'warning');
        if (!success) return handleSnackbarOpen("Opps.. Some Error occurred!", 'error')

        handleSnackbarOpen('Employee Id Verified! Please enter your registered Email ID', 'success');
        setIdSubmitted(true);
    }

    const onVerifyMail = async () => {
        const MailFormData = { employeeId: formData?.employeeId, emailId: formData?.emailId }
        setLoading(true);
        const { success, message } = await postData('/api/forgotPassword/verifyEmailId', MailFormData)
        setLoading(false);
        if (message) return handleSnackbarOpen(message, 'warning');
        if (!success) return handleSnackbarOpen("Opps.. Some Error occurred!", 'error');

        handleSnackbarOpen("We have sent a password reset link to your registered Email Id", 'success');
        navigate('/login')
    }
    const { formData, handleSubmit, handleChange } = useFormOperations({}, onSubmitId);

    return (
        <div className="relative flex justify-center items-center min-h-screen h-full md:p-10 md:px-20">

            {/* Wrapper for background split */}
            <div className="absolute inset-0 flex">
                <div className="w-1/2 bg-regal-purple-10 h-full"></div>
                <div className="flex w-1/2 bg-regal-purple-800 items-end justify-end h-full text-white text-opacity-50 text-sm lg:text-base p-2"> Crafted with ❤️ by the BytesWrite team </div>
            </div>

            {/* Main content container */}
            <div className="relative z-10 grid grid-cols-12 min-h-[670px] h-full w-full max-w-8xl drop-shadow-md md:rounded-3xl bg-white">

                <div className="lg:col-span-6 hidden lg:flex flex-col px-16 py-10 lg:py-20 gap-20">
                    <img draggable={false} className='hidden tab:block h-20 w-20 xl:h-28 xl:w-28' src={collegeLogos?.shortLight} alt="College Logo" />
                    <p className="text-base font-medium bg-clip-text text-transparent bg-gradient-to-r from-slate-700 to-slate-500">Seamlessly manage and verify student admission forms with precision, ensuring fast and accurate processing. Access essential details and documents instantly to streamline validation and enhance the admissions workflow.</p>
                    <div className='flex flex-col gap-10'>
                        <TypeAnimation
                            sequence={[
                                `Welcome to ${process.env.REACT_APP_COLLEGE_NAME}`,
                                2000,
                                `Welcome to ${process.env.REACT_APP_COLLEGE_NAME}`,
                                2000
                            ]}
                            wrapper="span"
                            speed={10}
                            style={{ fontSize: '1.5em', display: 'inline-block', fontWeight: 500, color: '#2C0A63' }}
                            repeat={Infinity}
                        />
                        <span className='text-base text-slate-700'>Built with ❤️ by the developers at Byteswrite</span>
                    </div>
                </div>

                <div className="col-span-full flex flex-col lg:col-span-6 items-center text-center w-full px-8 sm:px-10 py-10 lg:py-14 gap-10">
                    <header className='flex flex-col items-center select-none gap-2'>
                        <img draggable={false} className='block lg:hidden h-20 w-20' src={collegeLogos?.shortLight} alt="College Logo" />
                        <img draggable={false} className='w-48 sm:w-52 h-20' src="/logo/campus.svg" alt="CAMPUS_Logo" />
                        <p className='text-light text-xs sm:text-sm font-medium text-center max-w-xl w-full'>Achieve Academic Excellence with Us</p>
                    </header>

                    <div className='flex flex-col gap-4 max-w-md w-full' >
                        <form className='flex flex-col gap-6 w-full' onSubmit={handleSubmit}>
                            <CustomTextField
                                label="User ID"
                                name="employeeId"
                                value={formData?.employeeId ?? ''}
                                placeholder='Enter your official User ID'
                                fullWidth
                                disabled={idSubmitted}
                                startIcon={<AccountCircle className='text-regal-purple-600' />}
                                onChange={handleChange}
                            />
                            {idSubmitted ? <div className='flex flex-col gap-[7px] w-full'>
                                <CustomTextField
                                    name="emailId" label="Email Address" color="secondary" type="email" placeholder={employeeEmail ? `************${employeeEmail?.emailId}` : 'abc@example.com'}
                                    value={formData?.emailId ?? ''} onChange={handleChange} fullWidth
                                    startIcon={<Email className='text-regal-purple-600' />}
                                    endIcon={idSubmitted &&
                                        <ChangeCircle className="text-light hover:text-regal-purple-500 cursor-pointer" onClick={toggleIdSubmitted} />
                                    }
                                />
                                <span className="text-s self-start">Enter your Email Id to get password reset link</span>
                            </div> : <p className="h-[81px] grid place-items-center border-dashed border-2 w-full p-3">
                                <span className="text-center text-s leading-6">
                                    Forgot Password? <br />
                                    Reset your password by verifying your Email ID.
                                </span>
                            </p>}
                            {idSubmitted ?
                                <PurpleButton disabled={!(!!formData?.emailId) || !(!!formData?.employeeId)} size='large' fullWidth type="button" text="Verify Mail" onClick={onVerifyMail} sx={{ paddingX: 3, paddingY: 1.3 }} />
                                : <ConfirmButton fullWidth disabledElevation={true} size='large' type="submit" text="Submit ID" sx={{ paddingX: 3, paddingY: 1.3 }} />}
                        </form>

                        <Link to='/login?type=Password' className='text-s hover:text-regal-purple-500 hover:underline'>Continue to Login</Link>
                    </div>
                </div>

            </div>
        </div>
    )
}

export default ForgotPassword;